import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { LanguageProvider } from "./contexts/LanguageContext";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { initWebVitals } from "./lib/webVitals";
import { runLegacyMigration } from "./lib/legacyMigration";

// One-time Cashee → Wren legacy storage migration (no-op after first run)
runLegacyMigration();

// Expose test runner globals (runAllTests / runExpenseTests / runChatTests)
// on every page so they can be invoked from the browser console without
// first navigating to /debug/tests. Safe to load: only attaches helpers,
// does not auto-run anything, and protected backend still requires a JWT.
import("./tests/automated-test-runner")
  .then(() => console.log("[tests] Test runner loaded — call runExpenseTests() / runChatTests() / runAllTests()"))
  .catch((err) => console.warn("[tests] Failed to load automated test runner:", err));

// Initialize React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Persist query cache to localStorage for instant data on return visits
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'wren-query-cache',
  throttleTime: 1000,
});

// Register Service Worker for PWA with update detection
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(registration => {
      console.log('[SW] Registered successfully:', registration.scope);
      
      // Check for updates periodically (every 60 seconds)
      setInterval(() => registration.update(), 60000);
      
      // Detect when new SW is installed
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available — UpdateNotification UI was removed in Phase 2.
            console.log('[SW] New version available');
          }
        });
      });
    }).catch(error => {
      console.log('[SW] Registration failed:', error);
    });
  });
}

// Initialize Core Web Vitals monitoring
initWebVitals();

createRoot(document.getElementById("root")!).render(
  <LanguageProvider>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 }}>
      <App />
    </PersistQueryClientProvider>
  </LanguageProvider>
);
